import React, {Fragment, useEffect, useState} from 'react';
import {bool, func, object, string} from 'prop-types';
import {FormattedMessage, intlShape} from 'react-intl';
import classNames from 'classnames';
import {EmbeddedCheckoutProvider, EmbeddedCheckout} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import {map} from 'ramda';

import config from '../../../config';
import {IconArrowNavigation, IconCheck} from '../../Icons';
import {
  ModalSwimmy,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
} from '../../ModalSwimmy';
import css from './BuyBoostersPanelModal.module.css';
import {PrimaryButton} from '../..';
import {BOOSTERS_15, BOOSTERS_30, BOOSTERS_5} from '../../../constants/boosters';

const stripePromise = loadStripe(config.stripe.publishableKey);

const BuyBoostersPanelModal = (props) => {
  const {
    intl,
    isOpen,
    onCloseModal,
    onManageDisableScrolling,
    onCreateBoostersProductCheckoutSession,
    fetchBoostersCheckoutSessionInProgress,
    fetchBoostersCheckoutSessionError
  } = props;
  const [clientSecret, setClientSecret] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const options = {clientSecret};
  const handleSelectPlan = (e, plan) => {
    e.preventDefault();
    setSelectedPlan(plan);
  }
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedPlan) return;

    onCreateBoostersProductCheckoutSession(selectedPlan.planId)
      .then((clientSecret) => setClientSecret(clientSecret));
  }
  const handleCloseModal = (e) => {
    setSelectedPlan(null);
    setClientSecret(null);
    onCloseModal(e);
  }
  const handleGoBack = (e) => {
    if (!clientSecret) {
      handleCloseModal(e);
      return;
    }

    e.preventDefault();
    setSelectedPlan(null);
    setClientSecret(null);
  }
  const boosterPacks = [
    {
      planId: BOOSTERS_5,
      numberOfBoosters: 5,
      price: 5,
      isBest: false,
      discount: null,
    },
    {
      planId: BOOSTERS_15,
      numberOfBoosters: 15,
      price: 4,
      isBest: true,
      discount: '-20%',
    },
    {
      planId: BOOSTERS_30,
      numberOfBoosters: 30,
      price: 3,
      isBest: false,
      discount: '-40%',
    },
  ];
  const content = (
    <Fragment>
      <h2>
        <FormattedMessage id={'BoostYourListingsBuyModal.title'}/>
      </h2>
      <div className={css.description}>
        <FormattedMessage id={'BoostYourListingsBuyModal.description'}/>
      </div>
      <div className={css.boostersPackContainer}>
        {map((plan) => {
          const {planId, isBest, numberOfBoosters, price, discount} = plan;
          const isSelected = selectedPlan?.planId === planId;
          return (
            <div
              key={planId}
              className={classNames(css.boosterPackCard, isSelected && css.highlightedBoosterPackCard)}
              onClick={(e) => handleSelectPlan(e, plan)}
            >
              {isBest && (
                <div className={css.boosterPackBest}>
                  <FormattedMessage id={'BoostYourListingsBuyModal.best'}/>
                </div>
              )}
              {isSelected && <IconCheck className={css.iconCheck}/>}
              <div className={css.inline}>
                <div className={css.centerBlock}>
                  <div className={css.boosterPackNumber}>{numberOfBoosters}</div>
                  <div className={css.boosterPackLabel}>Boosters</div>
                </div>
                <div className={css.reverseOnResponsive}>
                  <div
                    className={css.boosterPackPrice}>{intl.formatMessage({id: 'BoostYourListingsBuyModal.priceEach'}, {priceEach: price})}</div>
                  {discount && (
                    <div className={css.boosterPackDiscount}>{discount}</div>
                  )}
                </div>
              </div>
            </div>
          )
        }, boosterPacks)}
      </div>
      {selectedPlan && (
        <div className={css.helper}>
          <FormattedMessage id={'BoostYourListingsBuyModal.helper'} values={{numberOfBoosters: selectedPlan.numberOfBoosters}}/>
        </div>
      )}
      {fetchBoostersCheckoutSessionError && (
        <div className={css.error}>
          <FormattedMessage id={'IncomePage.errorMessage'} />
        </div>
      )}
    </Fragment>
  );
  const checkoutView = (
    <div id="checkout" style={{width: '100%'}}>
      {clientSecret && (
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={options}
        >
          <EmbeddedCheckout/>
        </EmbeddedCheckoutProvider>
      )}
    </div>
  );

  return (
    <ModalSwimmy
      id="BuyBoostersPanelModal"
      animationType="slideRight"
      isOpen={isOpen}
      onClose={handleCloseModal}
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <ModalOverlay/>
      <ModalContent
        className={css.modalContent}
        animationType="slideRight"
        show={isOpen}
      >
        <ModalHeader className={css.modalHeader}>
          <button className={css.backButton} onClick={handleGoBack}>
            <IconArrowNavigation direction="left"/>
          </button>
          <div>
            <div className={classNames(css.stepIndicatorsContainer, clientSecret && css.rowReverse)}>
              <div className={css.stepActiveIndicator}/>
              <div className={css.stepInactiveIndicator}/>
            </div>
            <div className={css.newBadge}>
              <FormattedMessage id={'BoostYourListingsBuyModal.new'}/>
            </div>
          </div>
          <div className={css.spacer}/>
        </ModalHeader>
        <ModalBody className={classNames(css.modalBody, clientSecret && css.noPadding)}>
          {clientSecret ? checkoutView : content}
        </ModalBody>
        <div className={css.footer}>
          {!clientSecret && (
            <PrimaryButton
              className={css.primaryButton}
              onClick={handleSubmit}
              inProgress={fetchBoostersCheckoutSessionInProgress}
              disabled={fetchBoostersCheckoutSessionInProgress || !selectedPlan}
            >
              {selectedPlan ? (
                <FormattedMessage id={'BoostYourListingsBuyModal.cta'} values={{
                  numberOfBoosts: selectedPlan?.numberOfBoosters,
                  price: selectedPlan?.price * selectedPlan?.numberOfBoosters
                }}/>
              ) : (
                <FormattedMessage id={'BoostYourListingsBuyModal.ctaDisabled'}/>
              )}
            </PrimaryButton>
          )}
          <button
            disabled={fetchBoostersCheckoutSessionInProgress}
            type="button" className={css.clearButton} onClick={handleCloseModal}>
            <FormattedMessage id={'Marketplace.close'}/>
          </button>
        </div>
      </ModalContent>
    </ModalSwimmy>
  );
};

BuyBoostersPanelModal.defaultProps = {
  className: null,
  listing: null,
};

BuyBoostersPanelModal.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  isMobile: bool.isRequired,
  isOpen: bool.isRequired,
  listing: object,
  onCloseModal: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onSubmit: func.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
};

export default BuyBoostersPanelModal;
